import { Injectable } from "@angular/core";
import {
  IAppointment,
  IAppointmentRouting,
  IScheduledAppointmentContact,
  ITicket,
} from "../../../core-ui/models";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import {
  ActivityIndicatorService,
  AppointmentService,
  extractErrorMessage,
  PublicTicketService,
  ThemeService,
  UserService,
} from "../../../core-ui";
import {
  ErrorPageCode,
  ErrorPageSubCode,
  PARAM_APPOINTMENT_GUEST,
  PARAM_CONVERSATION_CHANNEL,
  PARAM_CONVERSATION_ORIGIN,
} from "../../app.enums";
import {
  ConversationOriginEnum,
  DEFAULT_APPOINTMENT_CHAT_MODE,
  PARAM_TICKET_ID,
  UserRoleEnum,
} from "../../../core-ui/core-ui.enums";
import { IConfigOptions, IContactInfo } from "@auvious/integrations";
import { ConversationService } from "../../../app/services/conversation.service";
import { ISyncable } from "./ISyncable";

class HandledError extends Error {
  subcode: string;
  code: any;

  constructor(
    code: ErrorPageCode,
    message?: string,
    subcode?: ErrorPageSubCode
  ) {
    super(message || code);
    this.code = code;
    this.subcode = subcode;
  }
}

@Injectable({ providedIn: "root" })
export class AppointmentConversationGuard implements ISyncable {
  constructor(
    private themeService: ThemeService,
    private ticketService: PublicTicketService,
    private appointmentService: AppointmentService,
    private conversation: ConversationService,
    private router: Router,
    private activity: ActivityIndicatorService,
    private user: UserService
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    this.activity.loading(true);

    let ticket: ITicket,
      appointment: IAppointment<IAppointmentRouting>,
      participant: IScheduledAppointmentContact,
      config: IConfigOptions,
      request: IContactInfo,
      appointmentId: string;

    // 1. get the ticket data
    try {
      ticket = await this.getTicket(route.paramMap.get(PARAM_TICKET_ID));
      if (!ticket.properties.appointmentId) {
        return true;
      }
      appointmentId = ticket.properties.appointmentId;
    } catch (ex) {
      return this.onError(ex);
    }

    // 2. update the status of the appointment to avoid race conditions
    try {
      appointment = await this.startAppointment(
        appointmentId,
        ticket.properties.customer_id
      );
    } catch (ex) {
      if (ex?.response?.status == 409) {
        // we have a conflict, call has already been initiated by another participant
        try {
          appointment = await this.getAppointment(appointmentId);
          // I did not start the conversatione
          if (
            !!appointment.interaction.initiatedBy &&
            appointment.interaction.initiatedBy.id !==
              ticket.properties.customer_id
          ) {
            return this.goToRoomAsGuestCustomer(state, ticket, appointment);
          }
          return this.onError(
            new HandledError(
              ErrorPageCode.APPOINTMENT_ERROR,
              "Could not join the appointment"
            ),
            appointmentId
          );
        } catch (e) {
          return this.onError(
            e,
            appointmentId,
            "Could not process appointment request"
          );
        }
      }
    }

    if (this.conversation.isConversationInitialised) {
      return true;
    }

    // 2. get the participant details
    try {
      participant = await this.appointmentService.getParticipantDetails(
        ticket.properties.appointmentId,
        ticket.properties.customer_id
      );
    } catch (ex) {
      return this.onError(
        ex,
        appointmentId,
        "Could not retrieve participant details"
      );
    }

    // 3. start a conversation (or reconnect to existing one)
    try {
      config = this.conversation.prepareConversationConfig(ticket, appointment);
      request = this.getConversationRequest(ticket, participant, appointment);

      await this.conversation.init(config);
      await this.conversation.connect(request);

      // was used by conferenceComponent.leaveApppointment' to end the conversation as customer
      // this.conversation.setConversationOriginCustomer(appointment.id);

      this.activity.loading(false);

      return true;
    } catch (ex) {
      return this.onError(
        ex,
        appointmentId,
        "Could not start a new conversation",
        config?.chatMode
      );
    }
  }

  private goToRoomAsGuestCustomer(
    state: RouterStateSnapshot,
    ticket: ITicket,
    appointment: IAppointment<IAppointmentRouting>
  ) {
    //   // mark user as guest customer
    const usr = this.user.getActiveUser();
    usr.addRole(UserRoleEnum.guestCustomer);
    this.user.setActiveUser(usr);

    // send to room
    const urlTree = state.url.split("/")?.filter((p) => !!p);
    const pagePath = urlTree.length > 0 ? urlTree[0] : "";

    return pagePath === "t"
      ? true
      : this.router.createUrlTree(["/t", ticket.id], {
          queryParams: {
            [PARAM_CONVERSATION_ORIGIN]: ConversationOriginEnum.APPOINTMENT,
            [PARAM_APPOINTMENT_GUEST]: 1,
            [PARAM_CONVERSATION_CHANNEL]:
              appointment.interaction.routing.properties.chatMode ||
              appointment.metadata.chatMode ||
              DEFAULT_APPOINTMENT_CHAT_MODE,
          },
        });
  }

  private async getTicket(ticket: string): Promise<ITicket> {
    try {
      if (!ticket) {
        throw new HandledError(
          ErrorPageCode.TICKET_NOT_FOUND,
          "Ticket not found in URL"
        );
      }

      const ticketData = await this.ticketService.getTicket(ticket);

      if (ticketData.properties.theme) {
        this.themeService.setOptions(ticketData.properties.theme);
      }

      return ticketData;
    } catch (ex) {
      throw new HandledError(
        ErrorPageCode.TICKET_NOT_FOUND,
        "Could not retrieve ticket details"
      );
    }
  }

  private async getAppointment(
    appointmentId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    try {
      const appointment = await this.appointmentService.get(appointmentId);
      this.handleAppointment(appointment);
      return appointment;
    } catch (ex) {
      if (ex instanceof HandledError) throw ex;
      else
        throw new HandledError(
          ErrorPageCode.APPOINTMENT_NOT_FOUND,
          "Could not retrieve appointment details"
        );
    }
  }

  /**
   * Updates the appointment interaction state to 'starting' to avoid a race condition with another participant
   * that may try to join the appointment at the same time.
   * If I am the one who started the appointment, I get back the appointment object
   * If I am a guest customer, I get back 409
   * @param appointmentId
   * @param participantId
   * @returns appointment
   */
  private async startAppointment(
    appointmentId: string,
    participantId: string
  ): Promise<IAppointment<IAppointmentRouting>> {
    try {
      const appointment = await this.appointmentService.updateState(
        appointmentId,
        "starting",
        { participantId }
      );
      this.handleAppointment(appointment);
      return appointment;
    } catch (ex) {
      if (ex instanceof HandledError) throw ex;
      if (ex?.response?.status === 409) throw ex;
      if (ex?.response?.status === 404)
        throw new HandledError(
          ErrorPageCode.APPOINTMENT_NOT_FOUND,
          "404: Could not update appointment state"
        );
      throw ex;
    }
  }

  private handleAppointment(appointment: IAppointment<IAppointmentRouting>) {
    if (!appointment) {
      throw new HandledError(
        ErrorPageCode.APPOINTMENT_NOT_FOUND,
        "Appointment could not be found"
      );
    }

    if (appointment.interaction.state === "COMPLETE") {
      throw new HandledError(
        ErrorPageCode.APPOINTMENT_COMPLETED,
        "Appointment is completed"
      );
    }

    if (appointment.interaction.state === "EXPIRED") {
      throw new HandledError(
        ErrorPageCode.APPOINTMENT_EXPIRED,
        "Appointment has expired"
      );
    }

    this.appointmentService.notifyChange(appointment);
  }

  private getConversationRequest(
    ticket: ITicket,
    participant: IScheduledAppointmentContact,
    appointment: IAppointment<IAppointmentRouting>
  ): IContactInfo {
    try {
      const request = this.conversation.prepareConversationRequest(
        ticket,
        appointment,
        participant
      );
      return request;
    } catch (ex) {
      throw new HandledError(ErrorPageCode.CONVERSATION_FAILED);
    }
  }

  private onError(
    error,
    appointmentId?: string,
    userMessage?: string,
    chatMode?: string
  ): UrlTree {
    this.activity.loading(false);

    let errorPageCode, code, message;

    if (error instanceof HandledError) {
      code = error.code;
      errorPageCode = error.code;
      message = error.message;
    } else {
      code =
        error.responseJSON.status ||
        error.response.status ||
        ErrorPageCode.GENERIC_ERROR;
      errorPageCode = ErrorPageCode.GENERIC_ERROR;
      message = extractErrorMessage(error);
    }
    // log error to appointment as well
    if (appointmentId && code !== ErrorPageCode.APPOINTMENT_COMPLETED) {
      let systemMessage = `Error code: ${code}, message: ${message}`;
      if (chatMode) systemMessage += `, integration: ${chatMode}`;
      // update state
      this.appointmentService.updateState(appointmentId, "failed", {
        systemMessage,
        userMessage,
      });
    }
    return this.router.createUrlTree(["/error", errorPageCode], {
      queryParams: { message: userMessage },
    });
  }
}
